import { useNavigate, Link } from 'react-router-dom';
import { Home, Loader2, type LucideIcon } from 'lucide-react';
import useApplyUITheme, { useSiteTheme } from '@/hooks/useApplyUITheme';

interface ErrorPageProps {
  /** Light or dark theme */
  mode?: 'light' | 'dark';
  /** Large error code displayed as the hero text (e.g. "404", "403") */
  title?: string;
  /** Subtitle below the error code */
  subtitle?: string;
  /** Longer explanation paragraph */
  description?: string;
  /** Primary CTA button label */
  primaryButtonText?: string;
  /** Secondary CTA button label */
  secondaryButtonText?: string;
  /** Icon shown inside the primary button */
  primaryButtonIcon?: LucideIcon;
  /** Icon shown inside the secondary button */
  secondaryButtonIcon?: LucideIcon;
  /** Route the primary button links to. Ignored when onPrimaryClick is given. */
  primaryButtonLink?: string;
  /** Custom click handler for the secondary button (overrides default navigate(-1)) */
  onSecondaryClick?: () => void;
  /** Click handler for the primary button. Renders it as a button rather than a link. */
  onPrimaryClick?: () => void;
  /** Disables both buttons, for while a handler is in flight */
  busy?: boolean;
  /** Icon size in px (default 20) */
  iconSize?: number;
}

export default function ErrorPage({
  mode = 'light',
  title = '404',
  subtitle = 'Oops! Page not found',
  description = "The page you're looking for seems to have wandered off into the digital void. Don't worry, even the best explorers get lost sometimes!",
  primaryButtonText = 'Dashboard',
  secondaryButtonText = 'Go Back',
  primaryButtonIcon: PrimaryIcon = Home,
  secondaryButtonIcon: SecondaryIcon,
  primaryButtonLink = '/',
  onSecondaryClick,
  onPrimaryClick,
  busy = false,
  iconSize = 20,
}: ErrorPageProps) {
  useApplyUITheme('auth');
  const { saved } = useSiteTheme('auth');
  const navigate = useNavigate();
  const isDark = mode === 'dark';

  const primaryColor = saved?.primary_color;
  const pageBg = saved?.page_background;

  return (
    <div
      className={`min-h-screen flex items-center justify-center transition-colors duration-500 ${
        !pageBg
          ? isDark
            ? 'bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900'
            : 'bg-gradient-to-br from-gray-100 via-white to-gray-100'
          : ''
      }`}
      style={pageBg ? { backgroundColor: pageBg } : undefined}
    >
      <div className="relative w-full max-w-2xl mx-4">
        {/* Background blur layer */}
        <div
          className={`absolute inset-0 backdrop-blur-xl rounded-2xl ${
            isDark ? 'bg-white/5' : 'bg-white/40'
          }`}
        />

        {/* Main Card */}
        <div
          className={`relative backdrop-blur-sm rounded-2xl border border-white/20 shadow-2xl p-8 sm:p-12 overflow-hidden transition-colors duration-500 ${
            isDark ? 'bg-white/10' : 'bg-white/50'
          }`}
        >
          {/* Gradient overlay */}
          <div
            className={`absolute inset-0 bg-gradient-to-br to-transparent pointer-events-none ${
              isDark ? 'from-white/20' : 'from-white/40'
            }`}
          />

          <div className="text-center space-y-6 sm:space-y-8">
            {/* Hero Title (uses pure Tailwind bg-primary-600) */}
            <div className="relative">
              <h1
                className={`text-7xl sm:text-9xl font-bold bg-clip-text text-transparent filter drop-shadow-lg ${
                  !primaryColor ? 'bg-primary-600' : ''
                }`}
                style={
                  primaryColor ? { backgroundColor: primaryColor } : undefined
                }
              >
                {' '}
                {title}
              </h1>
            </div>

            {/* Subtitle & Description */}
            <div className="space-y-4">
              <h2
                className={`text-xl sm:text-2xl font-semibold ${
                  isDark ? 'text-gray-200' : 'text-gray-800'
                }`}
              >
                {subtitle}
              </h2>
              <p
                className={`max-w-md mx-auto ${
                  isDark ? 'text-gray-300/80' : 'text-gray-600'
                }`}
              >
                {description}
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              {secondaryButtonText && (
                <button
                  onClick={onSecondaryClick || (() => navigate(-1))}
                  disabled={busy}
                  className={`group px-6 py-2 rounded-lg relative overflow-hidden transition-colors duration-300 disabled:opacity-60 disabled:cursor-not-allowed ${
                    isDark
                      ? 'bg-white/10 hover:bg-white/20'
                      : 'bg-gray-100 hover:bg-gray-200'
                  }`}
                >
                  <span
                    className={`relative flex justify-center items-center gap-2 ${
                      isDark ? 'text-gray-200' : 'text-gray-700'
                    }`}
                  >
                    {SecondaryIcon && <SecondaryIcon size={iconSize} />}
                    {secondaryButtonText}
                  </span>
                </button>
              )}
              {primaryButtonText &&
                (onPrimaryClick ? (
                  <button
                    onClick={onPrimaryClick}
                    disabled={busy}
                    className={`group px-6 py-2 rounded-lg relative overflow-hidden text-white transition-colors duration-300 disabled:opacity-60 disabled:cursor-not-allowed ${
                      !primaryColor ? 'bg-primary-600 hover:bg-primary-700' : ''
                    }`}
                    style={
                      primaryColor
                        ? { backgroundColor: primaryColor }
                        : undefined
                    }
                  >
                    <span className="relative flex justify-center items-center gap-2">
                      {busy ? (
                        <Loader2 size={iconSize} className="animate-spin" />
                      ) : (
                        PrimaryIcon && <PrimaryIcon size={iconSize} />
                      )}
                      {primaryButtonText}
                    </span>
                  </button>
                ) : (
                  <Link
                    to={primaryButtonLink}
                    className={`group px-6 py-2 rounded-lg relative overflow-hidden text-white transition-colors duration-300 ${
                      !primaryColor ? 'bg-primary-600 hover:bg-primary-700' : ''
                    }`}
                    style={
                      primaryColor
                        ? { backgroundColor: primaryColor }
                        : undefined
                    }
                  >
                    <span className="relative flex justify-center items-center gap-2">
                      {PrimaryIcon && <PrimaryIcon size={iconSize} />}
                      {primaryButtonText}
                    </span>
                  </Link>
                ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
